import React, { useState, useEffect, useCallback } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  CardHeader,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  Button,
  FormGroup,
  Spinner,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import BreadCrumb from '../../../Components/Common/BreadCrumb';

const SCOPE_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*\.[a-z0-9]+(-[a-z0-9]+)*$/;

const PermissionManagement = () => {
  const token = getAuthToken();
  const [data, setData] = useState({ items: [] });
  const [moduleOptions, setModuleOptions] = useState([]);
  const [modal, setModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  // Table States
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [moduleFilter, setModuleFilter] = useState(null);
  const [sortField, setSortField] = useState('scope');
  const [sortDirection, setSortDirection] = useState('asc');

  const initialFormState = {
    id: null,
    scope: '',
    module: '',
    description: '',
  };

  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchText(searchText), 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchModules = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${api.API_URL}/permission/modules`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      if (Array.isArray(result.data)) {
        setModuleOptions(result.data.map(m => ({ label: m, value: m })));
      }
    } catch (error) {
      console.error('Failed fetching permission modules', error);
    }
  }, [token]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const queryParams = {
        PageNumber: currentPage.toString(),
        PageSize: rowsPerPage.toString(),
      };

      const qs = new URLSearchParams(queryParams);

      if (debouncedSearchText && debouncedSearchText.trim() !== '') {
        qs.append('Search', debouncedSearchText.trim());
      }

      if (moduleFilter?.value) {
        qs.append('Module', moduleFilter.value);
      }

      if (sortField) {
        qs.append('SortBy', sortField);
        qs.append('IsDescending', sortDirection === 'desc' ? 'true' : 'false');
      }

      const response = await fetch(
        `${api.API_URL}/permission?${qs.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();
      setData({ items: result.data?.items || [] });
      setTotalRows(result.data?.totalItems || 0);
    } catch (error) {
      toast.error(`Error loading data: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    rowsPerPage,
    token,
    debouncedSearchText,
    moduleFilter,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const handleSearch = e => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const handleModuleFilterChange = opt => {
    setModuleFilter(opt);
    setCurrentPage(1);
  };

  const toggleModal = () => {
    if (modal) {
      setErrors({});
    }
    setModal(!modal);
  };

  const handleEdit = row => {
    setFormData({
      id: row.id,
      scope: row.scope || '',
      module: row.module || '',
      description: row.description || '',
    });
    setModal(true);
  };

  const handleDelete = async (id, scope) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Permission "${scope}" will be removed.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${api.API_URL}/permission/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message ||
              errorData.errors?.[0] ||
              'Delete operation failed'
          );
        }

        toast.success('Permission deleted successfully');
        fetchData();
      } catch (error) {
        toast.error(`Delete failed: ${error.message}`);
      }
    }
  };

  const validateForm = () => {
    const tempErrors = {};
    const isEdit = !!formData.id;

    if (!isEdit) {
      if (!formData.scope) {
        tempErrors.scope = 'Scope is required';
      } else if (!SCOPE_REGEX.test(formData.scope)) {
        tempErrors.scope =
          'Format must be resource.action (lowercase, hyphen-separated)';
      }
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setModalLoading(true);

    try {
      const isEdit = !!formData.id;
      const payload = isEdit
        ? { module: formData.module, description: formData.description }
        : {
            scope: formData.scope,
            module: formData.module,
            description: formData.description,
          };

      const url = isEdit
        ? `${api.API_URL}/permission/${formData.id}`
        : `${api.API_URL}/permission`;

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || errorData.errors?.[0] || 'Save operation failed'
        );
      }

      toast.success(
        `Permission ${isEdit ? 'updated' : 'created'} successfully`
      );
      toggleModal();
      fetchData();
      fetchModules();
    } catch (error) {
      toast.error(`Save failed: ${error.message}`);
    } finally {
      setModalLoading(false);
    }
  };

  const columns = [
    {
      id: 'scope',
      name: (
        <span className='text-uppercase fw-semibold fs-12 text-muted'>
          Scope
        </span>
      ),
      selector: row => row.scope,
      sortable: true,
      minWidth: '220px',
      cell: row => <code className='text-dark fw-medium'>{row.scope}</code>,
    },
    {
      id: 'module',
      name: (
        <span className='text-uppercase fw-semibold fs-12 text-muted'>
          Module
        </span>
      ),
      selector: row => row.module,
      sortable: true,
      minWidth: '160px',
      cell: row => <span>{row.module || '-'}</span>,
    },
    {
      name: (
        <span className='text-uppercase fw-semibold fs-12 text-muted'>
          Description
        </span>
      ),
      selector: row => row.description,
      minWidth: '260px',
      cell: row => <span className='text-muted'>{row.description || '-'}</span>,
    },
    {
      name: (
        <span className='text-uppercase fw-semibold fs-12 text-muted text-center w-100'>
          Action
        </span>
      ),
      width: '100px',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center'>
          <button
            className='btn btn-soft-info btn-sm btn-icon'
            onClick={() => handleEdit(row)}
          >
            <i className='ri-pencil-fill align-bottom'></i>
          </button>
          <button
            className='btn btn-soft-danger btn-sm btn-icon'
            onClick={() => handleDelete(row.id, row.scope)}
          >
            <i className='ri-delete-bin-6-line align-bottom'></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <React.Fragment>
      <BreadCrumb title='View Data' pageTitle='Permission Management' />
      <Row>
        <Col lg={12}>
          <Card className='border-0 shadow-sm'>
            <CardHeader className='border-0 pb-0'>
              <div className='d-flex align-items-center justify-content-between'>
                <h5 className='card-title mb-0'>Permission Management</h5>
                <Button
                  color='primary'
                  size='sm'
                  className='btn-label'
                  onClick={toggleModal}
                >
                  <i className='ri-add-line label-icon align-middle fs-16 me-2'></i>{' '}
                  Add Permission
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              <Row className='mb-3 g-2'>
                <Col md={4}>
                  <div className='search-box'>
                    <Input
                      type='text'
                      className='form-control'
                      placeholder='Search scope, module, or description...'
                      value={searchText}
                      onChange={handleSearch}
                    />
                    <i className='ri-search-line search-icon'></i>
                  </div>
                </Col>
                <Col md={3}>
                  <Select
                    options={moduleOptions}
                    isClearable
                    placeholder='Filter by module...'
                    value={moduleFilter}
                    onChange={handleModuleFilterChange}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                  />
                </Col>
              </Row>
              <DataTable
                columns={columns}
                data={data.items}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                onChangePage={page => setCurrentPage(page)}
                onChangeRowsPerPage={rows => {
                  setRowsPerPage(rows);
                  setCurrentPage(1);
                }}
                onSort={(column, dir) => {
                  setSortField(column.id);
                  setSortDirection(dir);
                  setCurrentPage(1);
                }}
                sortServer
                highlightOnHover
                responsive
                striped
                progressPending={isLoading}
                progressComponent={<Spinner color='primary' className='my-4' />}
                noDataComponent={
                  <p className='my-4 text-muted'>
                    No permission data available.
                  </p>
                }
              />
              <ToastContainer closeButton={false} limit={1} />
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal
        isOpen={modal}
        toggle={toggleModal}
        size='lg'
        backdrop='static'
        centered
        onClosed={() => setFormData(initialFormState)}
      >
        <ModalHeader toggle={toggleModal} className='bg-light'>
          {formData.id ? 'Edit Permission' : 'Create New Permission'}
        </ModalHeader>
        <ModalBody>
          <Row className='g-3'>
            <Col md={12}>
              <FormGroup>
                <Label>
                  Scope <span className='text-danger'>*</span>
                </Label>
                <Input
                  type='text'
                  placeholder='e.g. procurement.read-all'
                  value={formData.scope}
                  disabled={!!formData.id}
                  invalid={!!errors.scope}
                  onChange={e => {
                    setFormData(prev => ({
                      ...prev,
                      scope: e.target.value.trim(),
                    }));
                    if (errors.scope)
                      setErrors(prev => ({ ...prev, scope: undefined }));
                  }}
                />
                {errors.scope && (
                  <div className='text-danger fs-12 mt-1'>{errors.scope}</div>
                )}
                {formData.id && (
                  <div className='text-muted fs-12 mt-1'>
                    Scope tidak dapat diubah setelah dibuat.
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <Label>Module</Label>
                <Input
                  type='text'
                  placeholder='e.g. Planning & Procurement'
                  value={formData.module}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, module: e.target.value }))
                  }
                />
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <Label>Description</Label>
                <Input
                  type='textarea'
                  rows={3}
                  placeholder='Describe what this permission allows...'
                  value={formData.description}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color='light' onClick={toggleModal} disabled={modalLoading}>
            Cancel
          </Button>
          <Button color='primary' onClick={handleSave} disabled={modalLoading}>
            {modalLoading ? (
              <Spinner size='sm' className='me-2' />
            ) : (
              <i className='ri-save-line me-1'></i>
            )}
            {formData.id ? 'Save Changes' : 'Create Permission'}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default PermissionManagement;
